import { useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { type TFunction } from 'i18next';
import { ChevronRight, History } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAnnouncements } from '@/hooks/useAnnouncements';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useRecentModules } from '@/hooks/useRecentModules';
import { getAccessToken } from '@/lib/tokenStorage';
import { useCurrentUser, useSwitchDatabase } from '@/services/auth/util';

export type HomePageProps = {
  className?: string;
};

const MOCK_TICKETS = [
  {
    id: 'TKT-1042',
    title: 'Cannot export fert report to PDF',
    status: 'Open',
    statusColor: 'bg-yellow-100 text-yellow-700',
    date: '2026-06-24',
  },
  {
    id: 'TKT-1039',
    title: 'Sire search returns duplicates',
    status: 'In Progress',
    statusColor: 'bg-blue-100 text-blue-700',
    date: '2026-06-22',
  },
  {
    id: 'TKT-1035',
    title: 'Login timeout after 10 minutes idle',
    status: 'Resolved',
    statusColor: 'bg-green-100 text-green-700',
    date: '2026-06-20',
  },
  {
    id: 'TKT-1031',
    title: 'Plate view not loading for large runs',
    status: 'Open',
    statusColor: 'bg-yellow-100 text-yellow-700',
    date: '2026-06-19',
  },
  {
    id: 'TKT-1028',
    title: 'Missing translation on donor card',
    status: 'Resolved',
    statusColor: 'bg-green-100 text-green-700',
    date: '2026-06-18',
  },
];

function decodeJwt(token: string): Record<string, unknown> | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

export const HomePage = ({ className = '' }: HomePageProps) => {
  const { t } = useTranslation();
  const nav = useNavigate();
  const recentModules = useRecentModules();
  const { data: currentUser, isLoading: userLoading } = useCurrentUser();
  const queryClient = useQueryClient();
  const [switchDb, { isPending: isSwitching }] = useSwitchDatabase();
  const [switchingId, setSwitchingId] = useState<string | null>(null);
  const helpDeskEnabled = useFeatureFlag('nucleus_homepage_helpdesk');
  const whatsNewEnabled = useFeatureFlag('nucleus_homepage_whatsnew');
  const { data: announcements } = useAnnouncements(whatsNewEnabled);

  const token = getAccessToken();
  const decoded = token ? decodeJwt(token) : null;
  const canSwitchDb =
    decoded?.can_switch_database === 'true' ||
    decoded?.can_switch_database === true;
  const activeCompanyId = (decoded?.active_company_id as string) || null;

  const companies = currentUser?.companies;

  const activeCompany = useMemo(() => {
    if (!companies || !activeCompanyId) return null;
    return companies.find((c) => c.companyId === activeCompanyId);
  }, [companies, activeCompanyId]);

  const otherCompanies = useMemo(() => {
    if (!companies || !activeCompanyId) return [];
    return companies.filter((c) => c.companyId !== activeCompanyId);
  }, [companies, activeCompanyId]);

  const handleSwitchDb = async (companyId: string) => {
    try {
      setSwitchingId(companyId);
      await switchDb(companyId);
      await queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      window.location.reload();
    } catch {
      setSwitchingId(null);
    }
  };

  return (
    <div className={`flex flex-auto flex-col overflow-y-auto ${className}`}>
      <div className="mx-auto w-full p-4">
        {/* Welcome Banner */}
        <div className="mb-4 rounded-lg bg-gradient-to-r from-indigo-500 to-indigo-700 px-6 py-5 text-white">
          <h1 className="m-0 text-lg font-semibold">
            {t('home.welcomeToNucleus')}
          </h1>
          <p className="m-0 mt-1 text-sm text-indigo-100">
            {t('home.selectAppFromNavigation')}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* User + Company Card */}
          <div className="self-start rounded-lg border border-gray-200 bg-white">
            <div className="flex items-center gap-3 p-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-sm font-semibold text-indigo-600">
                {(currentUser?.displayName?.[0] ?? '?').toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="m-0 truncate text-sm font-medium text-gray-900">
                  {currentUser?.displayName ?? t('home.loading')}
                </p>
                <p className="m-0 mt-0.5 truncate text-xs text-gray-500">
                  {currentUser?.email ?? '—'}
                </p>
                <p className="m-0 truncate text-xs text-gray-500">
                  {activeCompany?.name ?? currentUser?.companyName ?? '—'}
                </p>
              </div>
              {/* Connection status dot */}
              <div className="flex items-center gap-1.5">
                <span
                  className={`inline-block h-2 w-2 rounded-full ${
                    userLoading
                      ? 'bg-yellow-400'
                      : currentUser
                        ? 'bg-green-500'
                        : 'bg-red-500'
                  }`}
                />
                <span className="text-xs text-gray-400">
                  {userLoading
                    ? t('home.connecting')
                    : currentUser
                      ? t('home.online')
                      : t('home.offline')}
                </span>
              </div>
            </div>

            {/* Company switcher (only for those with permission + multiple companies) */}
            {canSwitchDb && otherCompanies.length > 0 && (
              <div className="border-t border-gray-100 px-4 py-3">
                <p className="m-0 mb-2 text-xs font-medium tracking-wide text-gray-400 uppercase">
                  {t('home.switchCompany')}
                </p>
                <div className="flex flex-wrap gap-2">
                  {otherCompanies.map((company) => {
                    const loading =
                      isSwitching && switchingId === company.companyId;
                    return (
                      <button
                        key={company.companyId}
                        disabled={isSwitching}
                        className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:border-indigo-300 hover:bg-indigo-50 disabled:opacity-50"
                        onClick={() => handleSwitchDb(company.companyId)}
                      >
                        {loading && (
                          <svg
                            className="h-3 w-3 animate-spin text-indigo-500"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                            />
                          </svg>
                        )}
                        {company.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Recently Visited */}
          <div className="self-start overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div className="flex items-center gap-2 px-4 pt-3 pb-2">
              <h2 className="m-0 text-xs font-medium tracking-wide text-gray-400 uppercase">
                {t('home.recent', 'Recently visited')}
              </h2>
              {recentModules.length > 0 && (
                <span className="ml-auto text-xs text-gray-400">
                  {recentModules.length}
                </span>
              )}
            </div>
            {recentModules.length > 0 ? (
              <ul className="m-0 list-none divide-y divide-gray-100 border-t border-gray-100 p-0">
                {recentModules.map((mod) => (
                  <li key={mod.path}>
                    <button
                      type="button"
                      className="group flex w-full cursor-pointer items-center gap-3 border-0 bg-transparent px-4 py-2.5 text-left transition-colors hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none"
                      onClick={() => nav(mod.path)}
                    >
                      <span
                        className="flex size-8 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-semibold text-indigo-600"
                        aria-hidden
                      >
                        {(mod.label?.[0] ?? '?').toUpperCase()}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-gray-900 group-hover:text-indigo-600">
                          {mod.label}
                        </span>
                        <span className="block text-xs text-gray-500">
                          {formatTimeAgo(mod.visitedAt, t)}
                        </span>
                      </span>
                      <ChevronRight
                        className="size-4 shrink-0 text-gray-300 transition-transform group-hover:translate-x-0.5 group-hover:text-indigo-500"
                        aria-hidden
                      />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
                <span className="flex size-10 items-center justify-center rounded-full bg-gray-100">
                  <History className="size-5 text-gray-400" aria-hidden />
                </span>
                <p className="m-0 text-sm text-gray-500">
                  {t(
                    'home.emptyState',
                    'Pages you visit will show up here for quick access.',
                  )}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Announcements */}
        {whatsNewEnabled && announcements && announcements.length > 0 && (
          <div className="mt-4 rounded-lg border border-gray-200 bg-white">
            <div className="px-4 py-3">
              <h2 className="m-0 text-sm font-medium text-gray-700">
                {t('home.announcements')}
              </h2>
            </div>
            <div className="max-h-60 space-y-3 overflow-y-auto px-4 pb-4">
              {announcements.map((item) => (
                <div
                  key={item.id}
                  className="rounded-md border-[1.5px] border-gray-200 bg-white px-4 py-3"
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="m-0 text-sm font-medium text-gray-800">
                      {item.title}
                    </p>
                    <span className="shrink-0 text-xs text-gray-400">
                      {item.date}
                    </span>
                  </div>
                  <p className="m-0 mt-1 text-xs text-gray-500">{item.body}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Help Desk Tickets */}
        {helpDeskEnabled && (
          <div className="mt-4 rounded-lg border border-gray-200 bg-white">
            <div className="px-4 py-3">
              <h2 className="m-0 text-sm font-medium text-gray-700">
                {t('home.helpDesk')}{' '}
                <span className="text-xs font-normal text-gray-400">
                  ({t('home.comingSoon')})
                </span>
              </h2>
            </div>
            <div className="flex gap-3 overflow-x-auto px-4 pb-4">
              {MOCK_TICKETS.map((ticket) => (
                <div
                  key={ticket.id}
                  className="relative flex w-56 shrink-0 flex-col overflow-hidden rounded-md border-[1.5px] border-gray-200 bg-white px-4 py-3"
                >
                  <span className="pointer-events-none absolute inset-0 flex rotate-[-20deg] items-center justify-center text-2xl font-bold text-gray-100 uppercase select-none">
                    {t('home.example')}
                  </span>
                  <div className="relative flex items-center justify-between gap-2">
                    <span className="text-[10px] font-medium text-gray-400">
                      {ticket.id}
                    </span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${ticket.statusColor}`}
                    >
                      {ticket.status}
                    </span>
                  </div>
                  <p className="relative m-0 mt-2 line-clamp-2 text-sm font-medium text-gray-800">
                    {ticket.title}
                  </p>
                  <p className="relative m-0 mt-1 text-xs text-gray-400">
                    {ticket.date}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

function formatTimeAgo(timestamp: number, t: TFunction): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return t('home.justNow', 'Just now');
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60)
    return t('home.minutesAgo', {
      count: minutes,
      defaultValue: '{{count}}m ago',
    });
  const hours = Math.floor(minutes / 60);
  if (hours < 24)
    return t('home.hoursAgo', { count: hours, defaultValue: '{{count}}h ago' });
  const days = Math.floor(hours / 24);
  return t('home.daysAgo', { count: days, defaultValue: '{{count}}d ago' });
}
